import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck, FaEye, FaShieldAlt, FaTimes } from "react-icons/fa";
import {
  PRIVACY_CHANNELS,
  PRIVACY_VISIBILITY_OPTIONS,
  DEFAULT_VISIBILITY,
  buildInitialChannels,
} from "../../../../features/privacy/definitions";

const DEFAULT_CHANNEL_LABELS = {
  website: "Allow on website",
  newsletter: "Allow in newsletter",
  socials: "Allow on social media",
  promotionalMaterials: "Allow in promotional materials",
  internalPublications: "Allow in internal publications",
  localMedia: "Allow in local media",
  videoRecordings: "Allow video recordings",
};

const DEFAULT_VISIBILITY_LABELS = {
  VISIBLE: "Visible",
  NOT_VISIBLE: "Not visible",
  UNSET: "No preference set",
};

const ChildPrivacyForm = forwardRef(({ value, onSave, disabled, onDirtyChange }, ref) => {
  const { t } = useTranslation();
  const [channels, setChannels] = useState(() => buildInitialChannels(value));
  const [visibility, setVisibility] = useState(value?.visibility ?? DEFAULT_VISIBILITY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const visibilityGroupName = React.useMemo(
    () => `privacy-visibility-${Math.random().toString(36).slice(2, 10)}`,
    []
  );

  useEffect(() => {
    setChannels(buildInitialChannels(value));
    setVisibility(value?.visibility ?? DEFAULT_VISIBILITY);
  }, [value]);

  const isDirty = React.useMemo(() => {
    const channelsChanged = PRIVACY_CHANNELS.some(({ key }) => {
      const original = !!value?.[key];
      const current = !!channels[key];
      return original !== current;
    });
    const originalVisibility = value?.visibility ?? DEFAULT_VISIBILITY;
    return channelsChanged || originalVisibility !== visibility;
  }, [channels, value, visibility]);

  useEffect(() => {
    if (!onDirtyChange) return undefined;
    onDirtyChange(isDirty);
    return () => onDirtyChange(false);
  }, [isDirty, onDirtyChange]);

  const handleSave = async () => {
    if (!isDirty) return false;
    setErr(null);
    setSaving(true);
    try {
      const payload = PRIVACY_CHANNELS.reduce(
        (acc, { key }) => ({ ...acc, [key]: !!channels[key] }),
        {}
      );
      const response = {
        ...payload,
        visibility: visibility === "UNSET" ? null : visibility,
        ...(value?.id ? { id: value.id } : {}),
      };
      await onSave(response);
      return true;
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Failed to save");
      throw e;
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
    isDirty: () => isDirty,
  }));

  return (
    <div className="space-y-5 text-slate-900 dark:text-slate-100">
      <section className="rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/5">
        <div className="mb-4 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-200">
            <FaShieldAlt aria-hidden="true" />
          </span>
          <div>
            <h3 className="text-lg font-bold text-slate-950 dark:text-white">
              {t("privacy.channels", { defaultValue: "Publication channels" })}
            </h3>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
              {t("privacy.channels_hint", { defaultValue: "Choose where this child may be published." })}
            </p>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          {PRIVACY_CHANNELS.map(({ key, labelKey }) => {
            const checked = !!channels[key];
            return (
              <label
                key={key}
                className={`flex min-h-[72px] cursor-pointer items-center gap-3 rounded-[18px] border p-4 transition ${
                  checked
                    ? "border-emerald-200 bg-emerald-50/80 text-emerald-800 shadow-sm dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-100"
                    : "border-slate-200 bg-slate-50/70 text-slate-700 hover:border-indigo-200 hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
                } ${disabled || saving ? "cursor-not-allowed opacity-70" : ""}`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={checked}
                  onChange={(e) =>
                    setChannels((prev) => ({
                      ...prev,
                      [key]: e.target.checked,
                    }))
                  }
                  disabled={disabled || saving}
                />
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${
                    checked
                      ? "border-emerald-300 bg-white text-emerald-600 dark:border-emerald-300/40 dark:bg-white/10 dark:text-emerald-100"
                      : "border-slate-200 bg-white text-slate-400 dark:border-white/10 dark:bg-white/10"
                  }`}
                >
                  {checked ? <FaCheck aria-hidden="true" /> : <FaTimes aria-hidden="true" />}
                </span>
                <span className="text-sm font-semibold">
                  {t(labelKey, { defaultValue: DEFAULT_CHANNEL_LABELS[key] || key })}
                </span>
              </label>
            );
          })}
        </div>
      </section>

      <section className="rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/5">
        <div className="mb-4 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-50 text-sky-600 dark:bg-sky-400/15 dark:text-sky-200">
            <FaEye aria-hidden="true" />
          </span>
          <div>
            <h3 className="text-lg font-bold text-slate-950 dark:text-white">
              {t("privacy.visibility.title", { defaultValue: "Visibility" })}
            </h3>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          {PRIVACY_VISIBILITY_OPTIONS.map(({ value, labelKey }) => {
            const selected = visibility === value;
            return (
              <label
                key={value}
                className={`relative flex min-h-[68px] cursor-pointer items-center justify-center rounded-[18px] border px-4 text-center text-sm font-bold transition ${
                  selected
                    ? "border-indigo-300 bg-indigo-50 text-indigo-700 shadow-sm ring-2 ring-indigo-100 dark:border-indigo-300/40 dark:bg-indigo-400/15 dark:text-indigo-100 dark:ring-indigo-300/20"
                    : "border-slate-200 bg-slate-50/70 text-slate-600 hover:border-indigo-200 hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
                } ${disabled || saving ? "cursor-not-allowed opacity-70" : ""}`}
              >
                <input
                  type="radio"
                  name={visibilityGroupName}
                  value={value}
                  className="sr-only"
                  checked={selected}
                  onChange={() => setVisibility(value)}
                  disabled={disabled || saving}
                />
                {t(labelKey, {
                  defaultValue: DEFAULT_VISIBILITY_LABELS[value] || value,
                })}
                {selected && <span className="absolute inset-x-5 bottom-1.5 h-1 rounded-full bg-indigo-600 dark:bg-indigo-300" />}
              </label>
            );
          })}
        </div>

        {value?.updatedAt && (
          <p className="mt-4 text-xs font-medium text-slate-500 dark:text-slate-400">
            {t("privacy.last_updated", { defaultValue: "Last updated" })}:{" "}
            {new Date(value.updatedAt).toLocaleString()}
          </p>
        )}
      </section>

      {err && (
        <div className="rounded-[18px] border border-red-200 bg-red-50/80 p-4 text-sm font-semibold text-red-700 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-100">
          {err}
        </div>
      )}
    </div>
  );
});

ChildPrivacyForm.displayName = "ChildPrivacyForm";

export default ChildPrivacyForm;
